import React, { useState, useEffect, useMemo } from "react";
import {
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  PDFViewer,
  Font,
} from "@react-pdf/renderer";
import { getDoc, doc, updateDoc } from "firebase/firestore";
import { db, storage } from "../firebaseConfig";
import { ref, deleteObject } from "firebase/storage";
import EditarRemisionForm from "./EditarRemisionForm";
import OpenRegular from "../fonts/OpenSans-Regular.ttf";
import OpenBold from "../fonts/OpenSans-Bold.ttf";
import Swal from "sweetalert2";
import Modal from "react-modal";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";

/* ====== FUENTES ====== */
Font.register({
  family: "Open",
  fonts: [
    { src: OpenRegular, fontWeight: "normal" },
    { src: OpenBold, fontWeight: "bold" },
  ],
});
Font.registerHyphenationCallback((word) => [word]);

/* ====== ESTILOS PDF / MODAL ====== */
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 30,
    fontSize: 10,
    fontFamily: "Open",
  },
  pdfContainer: {
    position: "fixed",
    inset: 0,
    padding: "2vw",
    backgroundColor: "rgba(0,0,0,0.6)",
    zIndex: 1000,
    display: "grid",
    placeItems: "center",
  },
  pdfViewer: {
    width: "min(96vw, 1200px)",
    height: "min(94dvh, 1000px)",
    border: "1px solid #ccc",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#fff",
    boxShadow: "0 10px 30px rgba(0,0,0,.25)",
  },
  closeButton: { color: "#fff", top: "15px", fontSize: "20px" },

  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  logoContainer: { flexDirection: "column", alignItems: "flex-start" },
  logo: { width: 120, marginBottom: 5 },
  orderTitleContainer: { alignItems: "flex-end" },
  orderTitle: {
    fontSize: 14,
    color: "#3579CB",
    fontWeight: "bold",
    letterSpacing: "1px",
  },
  orderNumber: {
    fontSize: 11,
    backgroundColor: "#FF0000",
    color: "#fff",
    fontWeight: "bold",
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginTop: 2,
    letterSpacing: "0.5px",
  },

  infoSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  infoLeft: { width: "45%" },
  infoRight: { width: "45%", alignItems: "flex-end" },
  infoTitle: {
    fontWeight: "bold",
    fontSize: 10,
    marginBottom: 10,
    textDecoration: "underline",
  },
  infoText: { fontSize: 9, marginBottom: 2 },
  infoTextRazon: { fontSize: 9, marginBottom: 2, fontWeight: "bold" },

  dataSection: { flexDirection: "row", marginBottom: 10, gap: 2 },
  dataBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#eaeaea",
    backgroundColor: "#eee",
    padding: 8,
    justifyContent: "center",
  },
  dataBoxTitle: {
    fontSize: 8,
    color: "#555",
    textAlign: "center",
    letterSpacing: "0.1px",
  },
  dataBoxValue: {
    fontSize: 8,
    fontWeight: 600,
    marginTop: 2,
    textTransform: "uppercase",
    textAlign: "center",
    letterSpacing: "0.1px",
    minHeight: 10,
  },

  tableContainer: { marginTop: 10 },
  tableHeaderRow: { flexDirection: "row", backgroundColor: "#555" },
  tableHeaderCell: {
    paddingVertical: 6,
    paddingHorizontal: 4,
    fontSize: 9,
    backgroundColor: "#555",
    color: "#fff",
    textAlign: "center",
  },
  tableRow: { flexDirection: "row" },
  tableCell: {
    paddingVertical: 8,
    paddingHorizontal: 4,
    fontSize: 9,
    justifyContent: "center",
    textAlign: "right",
    flexWrap: "wrap",
    flexShrink: 1,
  },

  footerSection: {
    marginTop: 15,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  conditions: { width: "60%", fontSize: 9, lineHeight: 1.3 },
  totals: { width: "35%", fontSize: 9, minHeight: 100 },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 2,
  },
  totalLabel: {
    fontSize: 9,
    textAlign: "right",
    width: "45%",
    fontWeight: "bold",
  },
  totalValue: { fontSize: 9, paddingRight: 8 },
  totalBox: {
    backgroundColor: "#3579CB",
    paddingHorizontal: 8,
    paddingVertical: 7,
    marginTop: 6,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  totalBoxText: { color: "#fff", fontWeight: "bold", letterSpacing: "0.5px" },
  labelTotal: { color: "#fff", fontWeight: "bold", textAlign: "right" },

  contactInfoContainer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    backgroundColor: "#FFFFFF",
  },
  contactText: { fontSize: 9, textAlign: "left", marginBottom: 2 },
  contactEmail: { textDecoration: "underline" },

  montoGroup: { alignItems: "flex-end", marginTop: 20, paddingRight: 0 },
  montoEnLetrasLabel: {
    fontFamily: "Open",
    fontWeight: "bold",
    fontSize: 9,
    textAlign: "right",
    color: "#666",
  },
  montoEnLetras: {
    fontFamily: "Open",
    fontWeight: "bold",
    fontSize: 9,
    textAlign: "right",
    marginTop: 6,
  },
});

// Estilos inline para el botón (DOM, no PDF)
const backBtnStyle = {
  position: "absolute",
  top: 0,
  right: 12,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 12px",
  fontFamily: "inherit",
  fontSize: 14,
  fontWeight: 600,
  color: "#111", // texto negro
  background: "#f1f3f5",
  border: "1px solid #d0d7de",
  borderRadius: 8,
  cursor: "pointer",
  boxShadow: "0 1px 0 rgba(0,0,0,.04)",
  lineHeight: 1,
};

const backBtnIconStyle = { fontSize: 20, lineHeight: 0 };

/* ===== Helpers de cliente ===== */
const clean = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  const basura = /^(n\/a|na|n\.?d\.?|\.|-|s\/n|sn|null|undefined)$/i;
  return s && !basura.test(s) ? s : "";
};

const onlyDigits = (v) => (v || "").toString().replace(/\D+/g, "");

// Formato de dinero consistente: 1,234.56
const formatMoney = (n) => {
  const x = (Number(n) || 0).toFixed(2);
  const [i, d] = x.split(".");
  return i.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + d;
};

const getNombreEmpresa = (c) =>
  c?.empresa || c?.nombre || c?.razonSocial || c?.Nombre || "";

const getDireccionFields = (c) => ({
  calle: clean(c?.calle ?? c?.domicilio ?? c?.direccion?.calle),
  numeroExt: clean(
    c?.numeroExt ??
      c?.noExterior ??
      c?.numeroExterior ??
      c?.direccion?.numeroExt
  ),
  numeroInt: clean(
    c?.numeroInt ??
      c?.noInterior ??
      c?.numeroInterior ??
      c?.direccion?.numeroInt
  ),
  colonia: clean(c?.colonia ?? c?.direccion?.colonia),
  ciudad: clean(c?.ciudad ?? c?.direccion?.ciudad),
  estado: clean(c?.estado ?? c?.direccion?.estado),
  codigoPostal: onlyDigits(
    String(c?.codigoPostal ?? c?.cp ?? c?.direccion?.codigoPostal ?? "")
  ),
  rfc: clean(c?.rfc),
});

const direccionTieneAlgo = (d) =>
  !!(d.calle || d.colonia || d.ciudad || d.estado || d.codigoPostal);

/* ===== NÚMERO → TEXTO ===== */
const numeroATexto = (n) => {
  n = Math.floor(Math.abs(n));
  if (n === 0) return "CERO";
  const U = [
    "",
    "UNO",
    "DOS",
    "TRES",
    "CUATRO",
    "CINCO",
    "SEIS",
    "SIETE",
    "OCHO",
    "NUEVE",
  ];
  const E = {
    10: "DIEZ",
    11: "ONCE",
    12: "DOCE",
    13: "TRECE",
    14: "CATORCE",
    15: "QUINCE",
    16: "DIECISÉIS",
    17: "DIECISIETE",
    18: "DIECIOCHO",
    19: "DIECINUEVE",
  };
  const D = {
    20: "VEINTE",
    30: "TREINTA",
    40: "CUARENTA",
    50: "CINCUENTA",
    60: "SESENTA",
    70: "SETENTA",
    80: "OCHENTA",
    90: "NOVENTA",
  };
  const C = {
    100: "CIENTO",
    200: "DOSCIENTOS",
    300: "TRESCIENTOS",
    400: "CUATROCIENTOS",
    500: "QUINIENTOS",
    600: "SEISCIENTOS",
    700: "SETECIENTOS",
    800: "OCHOCIENTOS",
    900: "NOVECIENTOS",
  };
  const tens = (x) => {
    if (x < 10) return U[x];
    if (x <= 19) return E[x];
    if (x === 20) return "VEINTE";
    if (x < 30) {
      if (x === 22) return "VEINTIDÓS";
      if (x === 23) return "VEINTITRÉS";
      if (x === 26) return "VEINTISÉIS";
      return "VEINTI" + U[x - 20];
    }
    const de = Math.floor(x / 10) * 10;
    const un = x % 10;
    return D[de] + (un ? " Y " + U[un] : "");
  };
  const hundreds = (x) => {
    if (x === 100) return "CIEN";
    const h = Math.floor(x / 100) * 100;
    if (h) return C[h] + (x % 100 ? " " + tens(x % 100) : "");
    return tens(x);
  };
  let s = "";
  if (n >= 1_000_000) {
    const m = Math.floor(n / 1_000_000);
    s += m === 1 ? "UN MILLÓN" : `${numeroATexto(m)} MILLONES`;
    n %= 1_000_000;
    if (n) s += " ";
  }
  if (n >= 1000) {
    const k = Math.floor(n / 1000);
    s += k === 1 ? "MIL" : `${hundreds(k)} MIL`;
    n %= 1000;
    if (n) s += " ";
  }
  if (n > 0) s += hundreds(n);
  return s.trim();
};

const montoALetras = (monto, moneda = "PESOS", sufijo = "MXN") => {
  const totalCents = Math.round(Math.abs(monto) * 100);
  const entero = Math.floor(totalCents / 100);
  const cent = totalCents % 100;
  const fraccion = String(cent).padStart(2, "0");
  let literal = numeroATexto(entero);
  literal = literal
    .replace(/ UNO$/, " UN")
    .replace(/ VEINTIUNO$/, " VEINTIÚN")
    .replace(/ VEINTIUNO (?=MIL\b)/g, " VEINTIÚN ")
    .replace(/ UNO (?=MIL\b)/g, " UN ");
  const mon = moneda.toUpperCase();
  const monedaSingular =
    mon === "PESOS"
      ? "PESO"
      : mon === "DÓLARES" || mon === "DOLARES"
      ? "DÓLAR"
      : moneda;
  const usaDe = entero > 0 && entero % 1_000_000 === 0;
  const monedaFinal = entero === 1 ? monedaSingular : moneda;
  const monedaConDe = usaDe ? `DE ${monedaFinal}` : monedaFinal;
  return `${literal} ${monedaConDe} ${fraccion}/100 ${sufijo}`;
};

/* ====== COMPONENTE ====== */
const ResumenRemision = ({
  remision,
  isOpen,
  onClose,
  clientes,
  setRemisiones,
  remisiones,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [pdfVisible, setPdfVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [showAdjuntosModal, setShowAdjuntosModal] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);
  const [hoverBack, setHoverBack] = useState(false);

  // respaldo: si el cliente no viene en props.clientes, se carga por Firestore
  const [clienteDeFS, setClienteDeFS] = useState(null);

  const { formatDate } = useFunctionsPrevia();

  /* Overlay global */
  useEffect(() => {
    const cls = "printing-overlay";
    if (pdfVisible) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [pdfVisible]);

  useEffect(() => {
    if (showAdjuntosModal && remision?.archivos) setAdjuntos(remision.archivos);
  }, [remision, showAdjuntosModal]);

  /* Cargar cliente por ID si no está en props.clientes */
  useEffect(() => {
    const id = remision?.cliente || remision?.clienteId;
    if (!id) {
      setClienteDeFS(null);
      return;
    }
    const enProp = (clientes || []).find(
      (c) => c.id === id || c.__docId === id
    );
    if (enProp) {
      setClienteDeFS(enProp);
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, "clientes", id));
        setClienteDeFS(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch (e) {
        console.error("No pude leer el cliente por id:", e);
        setClienteDeFS(null);
      }
    })();
  }, [remision?.cliente, remision?.clienteId, clientes]);

  useEffect(() => {
    if (!isOpen && onClose) onClose();
  }, [isOpen, onClose]);

  const handleDeleteArchivo = async (archivo) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Estás a punto de eliminar el archivo "${archivo.nombre}". Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (result.isConfirmed) {
      try {
        const fileRef = ref(storage, archivo.url);
        await deleteObject(fileRef);
        const remisionRef = doc(db, "remisiones", remision.id);
        await updateDoc(remisionRef, {
          archivos: remision.archivos.filter((a) => a.url !== archivo.url),
        });
        setAdjuntos((prev) => prev.filter((a) => a.url !== archivo.url));
        if (typeof setRemisiones === "function") {
          setRemisiones((prev) =>
            prev.map((c) =>
              c.id === remision.id
                ? {
                    ...c,
                    archivos: c.archivos.filter((a) => a.url !== archivo.url),
                  }
                : c
            )
          );
        }
        Swal.fire({
          icon: "success",
          title: "Archivo eliminado",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error al eliminar el archivo:", error);
        Swal.fire({
          icon: "error",
          title: "Error al eliminar",
          text: "Hubo un problema al eliminar el archivo.",
        });
      }
    }
  };

  const obtenerIconoArchivo = (nombreArchivo) => {
    const ext = (nombreArchivo || "").split(".").pop().toLowerCase();
    const map = {
      xls: ["bi-file-earmark-excel", "green"],
      xlsx: ["bi-file-earmark-excel", "green"],
      csv: ["bi-file-earmark-excel", "green"],
      doc: ["bi-file-earmark-word", "blue"],
      docx: ["bi-file-earmark-word", "blue"],
      pdf: ["bi-file-earmark-pdf", "red"],
      jpg: ["bi-image", "blue"],
      jpeg: ["bi-image", "blue"],
      png: ["bi-image", "blue"],
      svg: ["bi-image", "blue"],
    };
    const [icon, color] = map[ext] || ["bi-file-earmark", "gray"];
    return { icon, color };
  };

  const handlePrint = () => setPdfVisible(true);
  const handleClosePDF = () => setPdfVisible(false);

  /* ====== PDF MEMO ====== */
  const memoizedPdfDocument = useMemo(() => {
    if (!remision || !remision.productosSeleccionados?.length) return null;

    // === Cliente: SOLO por ID (props o Firestore) ===
    const id = remision?.cliente || remision?.clienteId;
    const cliente =
      (clientes || []).find((c) => c.id === id || c.__docId === id) ||
      clienteDeFS ||
      null;

    const nombreEmpresa =
      remision?.nombreCliente || getNombreEmpresa(cliente || {});
    const fields = getDireccionFields(cliente || {});
    const {
      calle,
      numeroExt,
      numeroInt,
      colonia,
      ciudad,
      estado,
      codigoPostal,
    } = fields;
    const showAddress = direccionTieneAlgo(fields);
    const rfc = clean(fields.rfc || remision?.rfc);

    // Dirección formateada
    const linea1Parts = [];
    if (calle) {
      let s = calle;
      if (numeroExt) s += ` ${numeroExt}`;
      if (numeroInt) s += ` Int. ${numeroInt}`;
      linea1Parts.push(s);
    }
    if (colonia) linea1Parts.push(colonia);
    const linea1 = linea1Parts.join(", ");
    const linea2 = [ciudad, estado].filter(Boolean).join(", ");

    // Paginación
    const productosPorPagina = 25;
    const paginasDeProductos = [];
    for (
      let i = 0;
      i < remision.productosSeleccionados.length;
      i += productosPorPagina
    ) {
      paginasDeProductos.push(
        remision.productosSeleccionados.slice(i, i + productosPorPagina)
      );
    }

    // Totales
    const subtotal = remision.productosSeleccionados.reduce((acc, p) => {
      const tarifa = parseFloat(p.tarifa ?? 0) || 0;
      const cantidad = parseFloat(p.cantidad ?? 0) || 0;
      const precioBruto = tarifa * cantidad;
      let desc = 0;
      if (p.tipoDescuento === "%")
        desc = precioBruto * ((parseFloat(p.descuento ?? 0) || 0) / 100);
      else if (p.tipoDescuento === "$")
        desc = parseFloat(p.descuento ?? 0) || 0;
      return acc + (precioBruto - desc);
    }, 0);

    const descuentoPorcentaje = parseFloat(remision.descuento ?? 0) || 0;
    const descuentoAplicado = subtotal * (descuentoPorcentaje / 100);
    const subtotalConDescuento = subtotal - descuentoAplicado;
    const ivaRate = 0.16;
    const ivaTotal = subtotalConDescuento * ivaRate;
    const total = subtotalConDescuento + ivaTotal;

    const divisa = (remision.divisa || "MXN").toUpperCase();
    const monedaPalabra = divisa === "USD" ? "DÓLARES" : "PESOS";
    const totalEnLetras = montoALetras(total, monedaPalabra, divisa);

    const len = totalEnLetras.length;
    const letrasFontSize = len > 115 ? 6 : len > 100 ? 7 : len > 85 ? 8 : 9;

    return (
      <Document>
        {paginasDeProductos.map((productosPagina, pageIndex) => (
          <Page key={pageIndex} size="A4" style={styles.page}>
            {/* Encabezado */}
            <View style={styles.headerContainer}>
              <View style={styles.logoContainer}>
                <Image src="/img/logo-iciamex2.png" style={styles.logo} />
              </View>
              <View style={styles.orderTitleContainer}>
                <Text style={styles.orderTitle}>REMISIÓN</Text>
                <Text style={styles.orderNumber}>
                  #{remision.numeroRemision?.toString().padStart(4, "0")}
                </Text>
              </View>
            </View>

            <View style={{ marginBottom: 10 }}>
              <View
                style={{
                  borderTopWidth: 2,
                  borderTopColor: "#3579CB",
                  borderColor: "#000",
                  position: "relative",
                  borderWidth: "0.4px",
                  backgroundColor: "#3579CB",
                }}
              />
              <View
                style={{
                  position: "absolute",
                  top: -3,
                  left: 0,
                  right: 0,
                  height: 3,
                  backgroundColor: "#3579CB",
                  width: "25%",
                }}
              />
            </View>

            {/* Cliente / Entrega */}
            <View style={styles.infoSection}>
              <View style={styles.infoLeft}>
                <Text style={styles.infoTitle}>CLIENTE:</Text>
                <Text style={styles.infoTextRazon}>
                  {nombreEmpresa || "N/D"}
                </Text>

                {showAddress && (
                  <>
                    {!!linea1 && <Text style={styles.infoText}>{linea1}</Text>}
                    {!!linea2 && (
                      <Text style={styles.infoText}>{`${linea2}.`}</Text>
                    )}
                    {!!codigoPostal && (
                      <Text
                        style={styles.infoText}
                      >{`C.P. ${codigoPostal}`}</Text>
                    )}
                  </>
                )}

                {!!rfc && <Text style={styles.infoText}>RFC: {rfc}</Text>}
              </View>

              <View style={styles.infoRight}>
                <Text style={styles.infoTitle}>ENTREGA:</Text>
                <Text style={styles.infoTextRazon}>
                  UNIFICACION PARA LA AUTOMATIZACION
                </Text>
                <Text style={styles.infoText}>
                  LIC. RAMIRO HERNÁNDEZ 448, COL. LAS PINTITAS,
                </Text>
                <Text style={styles.infoText}>EL SALTO, JAL. C.P. 45693</Text>
                <Text style={styles.infoText}>RFC: UAU151222IT1</Text>
              </View>
            </View>

            {/* Datos principales */}
            <View style={styles.dataSection}>
              <View style={styles.dataBox}>
                <Text style={styles.dataBoxTitle}>FECHA:</Text>
                <Text style={styles.dataBoxValue}>
                  {formatDate(remision.fechaRemision)}
                </Text>
              </View>
              <View style={styles.dataBox}>
                <Text style={styles.dataBoxTitle}>COTIZACIÓN:</Text>
                <Text style={styles.dataBoxValue}>
                  {remision.numeroCotizacion || "N/A"}
                </Text>
              </View>
              <View style={styles.dataBox}>
                <Text style={styles.dataBoxTitle}>INCOTERM:</Text>
                <Text style={styles.dataBoxValue}>
                  {remision.incoterm || "EXW"}
                </Text>
              </View>
              <View style={styles.dataBox}>
                <Text style={styles.dataBoxTitle}>DIVISA:</Text>
                <Text style={styles.dataBoxValue}>{remision.divisa}</Text>
              </View>
              <View style={styles.dataBox}>
                <Text style={styles.dataBoxTitle}>COND. PAGO:</Text>
                <Text style={styles.dataBoxValue}>{remision.condicion}</Text>
              </View>
              <View style={styles.dataBox}>
                <Text style={styles.dataBoxTitle}>AGENTE:</Text>
                <Text style={styles.dataBoxValue}>
                  {remision.vendedorNombre}
                </Text>
              </View>
            </View>

            {/* Tabla */}
            <View style={styles.tableContainer}>
              <View style={styles.tableHeaderRow}>
                <Text style={[styles.tableHeaderCell, { flex: 0.5 }]}>PDA</Text>
                <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                  NO. PARTE
                </Text>
                <Text style={[styles.tableHeaderCell, { flex: 5 }]}>
                  DESCRIPCIÓN
                </Text>
                <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                  CANTIDAD
                </Text>
                <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                  PRECIO
                </Text>
                <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                  SUBTOTAL
                </Text>
              </View>

              {productosPagina.map((producto, index) => (
                <View
                  key={index}
                  style={[
                    styles.tableRow,
                    { backgroundColor: index % 2 === 0 ? "#fff" : "#EFF2F2" },
                  ]}
                >
                  <Text
                    style={[
                      styles.tableCell,
                      { flex: 0.5, textAlign: "center" },
                    ]}
                  >
                    {index + 1 + pageIndex * 25}
                  </Text>
                  <Text
                    style={[styles.tableCell, { flex: 2, textAlign: "center" }]}
                  >
                    {producto.modelo || producto.numeroDeParteCliente || ""}
                  </Text>
                  <Text
                    style={[styles.tableCell, { flex: 5, textAlign: "left" }]}
                  >
                    {producto.nombre || "Sin descripción"}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      { flex: 1.5, textAlign: "center" },
                    ]}
                  >
                    {(parseFloat(producto.cantidad ?? 0) || 0).toString()}
                  </Text>
                  <Text
                    style={[styles.tableCell, { flex: 2, textAlign: "center" }]}
                  >
                    $
                    {(parseFloat(producto.tarifa ?? 0) || 0)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </Text>
                  <Text
                    style={[styles.tableCell, { flex: 2, textAlign: "center" }]}
                  >
                    {(parseFloat(producto.importe ?? 0) || 0)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </Text>
                </View>
              ))}
            </View>

            <View>
              <View
                style={{
                  border: "0.5px solid #3579CB",
                  backgroundColor: "#3579CB",
                  marginTop: 0,
                }}
              />
            </View>

            {/* Totales */}
            {pageIndex === paginasDeProductos.length - 1 && (
              <>
                <View style={styles.footerSection}>
                  <View style={styles.conditions}>
                    <Text
                      style={{
                        fontSize: 9,
                        fontWeight: "bold",
                        marginBottom: 10,
                      }}
                    >
                      CONDICIONES / OBSERVACIONES:
                    </Text>
                    <Text>{remision.notas}</Text>
                  </View>

                  <View style={styles.totals}>
                    <View style={styles.totalRow}>
                      <Text style={styles.totalLabel}>Subtotal</Text>
                      <Text style={styles.totalValue}>
                        ${formatMoney(subtotal)}
                      </Text>
                    </View>

                    {descuentoAplicado > 0 && (
                      <>
                        <View style={styles.totalRow}>
                          <Text style={styles.totalLabel}>
                            Descuento ({descuentoPorcentaje}%)
                          </Text>
                          <Text style={styles.totalValue}>
                            -$
                            {descuentoAplicado
                              .toFixed(2)
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                          </Text>
                        </View>
                        <View style={styles.totalRow}>
                          <Text style={styles.totalLabel}>Restante</Text>
                          <Text style={styles.totalValue}>
                            $
                            {subtotalConDescuento
                              .toFixed(2)
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                          </Text>
                        </View>
                      </>
                    )}

                    <View style={styles.totalRow}>
                      <Text style={styles.totalLabel}>IVA</Text>
                      <Text style={styles.totalValue}>
                        $
                        {ivaTotal.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Text>
                    </View>

                    <View style={[styles.totalRow, styles.totalBox]}>
                      <Text
                        style={[
                          styles.labelTotal,
                          { width: total > 100000 ? "40%" : "45%" },
                        ]}
                      >
                        TOTAL {divisa}
                      </Text>
                      <Text style={styles.totalBoxText}>
                        ${formatMoney(total)}
                      </Text>
                    </View>

                    <View style={styles.montoGroup}>
                      <Text style={styles.montoEnLetrasLabel}>
                        IMPORTE TOTAL CON LETRA
                      </Text>
                      <Text
                        wrap={false}
                        style={[
                          styles.montoEnLetras,
                          { fontSize: letrasFontSize },
                        ]}
                      >
                        {totalEnLetras}
                      </Text>
                    </View>
                  </View>
                </View>

                <View style={{ marginTop: 80 }}>
                  <View style={{ width: "50%", alignItems: "flex-start" }}>
                    <View
                      style={{
                        border: "1px solid #000",
                        borderWidth: "0.4px",
                        width: "50%",
                      }}
                    />
                    <Text
                      style={{
                        fontSize: 9,
                        textAlign: "center",
                        marginTop: 5,
                        marginLeft: 18,
                      }}
                    >
                      Firma de conformidad
                    </Text>
                  </View>
                </View>

                <View style={styles.contactInfoContainer}>
                  <Text style={styles.contactText}>
                    Para cualquier duda, comunicarse al teléfono:
                  </Text>
                  <Text style={styles.contactText}>
                    33 2192 8621 / 33 3619 2439 ó al correo{" "}
                    <Text style={styles.contactEmail}>victor@iciamex.com</Text>
                  </Text>
                </View>
              </>
            )}

            {/* Footer fijo */}
            <View
              fixed
              style={{
                position: "absolute",
                bottom: 0,
                left: 30,
                right: 30,
                flexDirection: "column",
                alignItems: "stretch",
                backgroundColor: "#FFFFFF",
              }}
            >
              <View
                style={{
                  borderTopWidth: 0.5,
                  borderTopColor: "#999999",
                  marginTop: 5,
                  paddingTop: 5,
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <Text style={{ fontSize: 8, color: "#999999" }}>
                    UNIFICACION PARA LA AUTOMATIZACION SA DE CV
                  </Text>
                  <Text
                    style={{ fontSize: 8, color: "#999999" }}
                    render={({ pageNumber }) => `${pageNumber}`}
                  />
                </View>
              </View>
            </View>
          </Page>
        ))}
      </Document>
    );
  }, [remision, clientes, clienteDeFS, formatDate]);

  /* ====== GENERAR BLOB ====== */
  useEffect(() => {
    let isMounted = true;
    let url;
    const generateBlobUrl = async () => {
      if (!memoizedPdfDocument) {
        setPdfBlobUrl(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const blob = await pdf(memoizedPdfDocument).toBlob();
        if (isMounted) {
          url = URL.createObjectURL(blob);
          setPdfBlobUrl(url);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    generateBlobUrl();
    return () => {
      isMounted = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [memoizedPdfDocument]);

  /* ====== UI ====== */
  if (pdfVisible) {
    return (
      <div style={styles.pdfContainer}>
        <button
          className="cerrar-button"
          style={styles.closeButton}
          onClick={handleClosePDF}
        >
          <i className="bi bi-x-lg"></i>
        </button>
        <PDFViewer className="pdf-viewer" style={styles.pdfViewer}>
          {memoizedPdfDocument}
        </PDFViewer>
      </div>
    );
  }

  const handleEdit = () => {
    setEditMode(true);
    setShowSummary(false);
  };
  const closeModal = () => {
    setEditMode(false);
    setShowSummary(true);
    onClose();
  };

  if (editMode) {
    return (
      <EditarRemisionForm
        remision={remision}
        clientes={clientes}
        productos={remision && remision.productosSeleccionados}
        setRemisiones={setRemisiones}
        remisiones={remisiones}
        onClose={closeModal}
      />
    );
  }

  if (showSummary && remision && remision.productosSeleccionados) {
    return (
      <div className="resumen-cotizacion-container">
        {/* Header de acciones: izquierda = Regresar / derecha = botones azules */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 10,
            marginBottom: 10,
            flexWrap: "wrap",
            position: "relative",
            top: "2px",
          }}
        >
          <button
            onClick={onClose}
            title="Regresar a tabla"
            onMouseEnter={() => setHoverBack(true)}
            onMouseLeave={() => setHoverBack(false)}
            style={{
              ...backBtnStyle,
              position: "static", // <-- anula el absolute de tu backBtnStyle
              marginTop: "2px",
              background: hoverBack ? "#e9ecef" : backBtnStyle.background,
              border: "1px solid #d0d7de",
              borderRadius: 8,
            }}
          >
            <i className="bi bi-arrow-left-short" style={backBtnIconStyle}></i>
            <span>Regresar a tabla</span>
          </button>

          <div
            className="resumen-cotizacion-actions"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              columnGap: 6,
              rowGap: 6,
              flexWrap: "wrap",
            }}
          >
            <div
              className="dropdown"
              onMouseEnter={() => setShowOptions(true)}
              onMouseLeave={() => setShowOptions(false)}
              style={{ margin: 0 }}
            >
              <button
                className="dropbtn"
                style={{
                  margin: 0,
                  border: "1px solid #d0d7de",
                  borderRadius: 8,
                }}
              >
                <i className="bi bi-printer iconResumen"></i> Imprimir / PDF
              </button>

              {showOptions && (
                <div className="dropdown-content">
                  <button
                    onClick={handlePrint}
                    style={{ border: "1px solid #d0d7de", borderRadius: 8 }}
                  >
                    <i
                      className="bi bi-file-earmark-text"
                      style={{ marginRight: 10 }}
                    />
                    Imprimir
                  </button>

                  <a
                    href={pdfBlobUrl || "#"}
                    download={`${remision.numeroCotizacion || "sin_numero"}-${
                      remision.nombreCliente || "sin_nombre"
                    }.pdf`}
                    style={{ textDecoration: "none" }}
                    onClick={(e) => {
                      if (!pdfBlobUrl) e.preventDefault();
                    }}
                  >
                    <button
                      style={{ border: "1px solid #d0d7de", borderRadius: 8 }}
                    >
                      <i
                        className="bi bi-download"
                        style={{ marginRight: 10 }}
                      />
                      Descargar PDF
                    </button>
                  </a>
                </div>
              )}
            </div>

            <button
              style={{
                margin: 0,
                border: "1px solid #d0d7de",
                borderRadius: 8,
              }}
            >
              <i className="bi bi-envelope-fill iconResumen"></i> Correo
            </button>

            <button
              onClick={handleEdit}
              style={{
                margin: 0,
                border: "1px solid #d0d7de",
                borderRadius: 8,
              }}
            >
              <i className="bi bi-pencil-fill iconResumen"></i> Editar
            </button>

            <button
              style={{
                margin: 0,
                border: "1px solid #d0d7de",
                borderRadius: 8,
              }}
            >
              <i className="bi bi-receipt iconResumen"></i> Facturar
            </button>

            {remision.archivos && remision.archivos.length > 0 && (
              <button
                onClick={() => setShowAdjuntosModal(true)}
                style={{
                  margin: 0,
                  border: "1px solid #d0d7de",
                  borderRadius: 8,
                }}
              >
                <i className="bi bi-paperclip iconResumen"></i> Archivos
                Adjuntos
              </button>
            )}
          </div>
        </div>

        <Modal
          isOpen={showAdjuntosModal}
          onRequestClose={() => setShowAdjuntosModal(false)}
          contentLabel="Archivos Adjuntos"
          style={{
            content: {
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
              width: "35%",
              maxHeight: "80vh",
              overflowY: "auto",
              padding: "20px",
              borderRadius: "10px",
            },
            overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
          }}
        >
          <button
            onClick={() => setShowAdjuntosModal(false)}
            className="modal-close"
            title="Cerrar"
          >
            <i className="bi bi-x-lg"></i>
          </button>

          <h3
            style={{
              marginBottom: "20px",
              color: "#000",
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Archivos Adjuntos
          </h3>

          <ul style={{ listStyle: "none", padding: 0 }}>
            {adjuntos.map((archivo, index) => {
              const { icon, color } = obtenerIconoArchivo(archivo.nombre);
              return (
                <li
                  key={index}
                  style={{
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <i
                      className={`bi ${icon}`}
                      style={{ color, fontSize: "1.5rem", marginRight: "10px" }}
                    />
                    <a
                      href={archivo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "none",
                        color: "#000",
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {archivo.nombre}
                    </a>
                  </div>

                  <div style={{ display: "flex", alignItems: "center" }}>
                    <a href={archivo.url} download={archivo.nombre}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          const link = document.createElement("a");
                          link.href = archivo.url;
                          link.target = "_blank";
                          link.rel = "noopener noreferrer";
                          link.download = archivo.nombre;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#007bff",
                          cursor: "pointer",
                          fontSize: "20px",
                        }}
                        title="Descargar archivo"
                      >
                        <i className="bi bi-download"></i>
                      </button>
                    </a>
                    <button
                      onClick={() => handleDeleteArchivo(archivo)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                        marginRight: "10px",
                        fontSize: "20px",
                      }}
                      title="Eliminar archivo"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Modal>

        <div
          style={{
            height: "clamp(360px, calc(100dvh - 220px), 900px)",
            width: "100%",
            marginTop: 0,
            display: "grid",
            placeItems: "center",
          }}
        >
          {loading ? (
            <div className="loader2"></div>
          ) : (
            pdfBlobUrl && (
              <iframe
                src={`${pdfBlobUrl}#toolbar=0`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: 0,
                  borderRadius: 4,
                  boxShadow: "0 6px 20px rgba(0,0,0,.15)",
                }}
                frameBorder="0"
                title="Vista previa del PDF"
              />
            )
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default ResumenRemision;
